import type { RouteObject } from "react-router";

import { WEB_WORKSPACE_ROLES } from "../auth/roles";
import { RouteAccessGate } from "../auth/RouteAccessGate";
import { AppShell } from "../components/layout/AppShell";
import { NotFoundScreen } from "../screens/NotFoundScreen";
import { PlannedWorkspaceScreen } from "../screens/PlannedWorkspaceScreen";
import { RouteErrorScreen } from "../screens/RouteErrorScreen";
import { SignInScreen } from "../screens/SignInScreen";
import { WorkspaceEntryScreen } from "../screens/WorkspaceEntryScreen";
import {
  AccountScreen,
  AssistedRecoveryScreen,
  AuthenticatorReplacementScreen,
  BrandingSettingsScreen,
  BusinessSettingsScreen,
  CashiersScreen,
  CatalogueScreen,
  DevicesScreen,
  InvitationAcceptScreen,
  LegalDocumentScreen,
  OverviewScreen,
  PasswordResetScreen,
  ReportsScreen,
  ShiftsScreen,
  ShopSettingsScreen,
  StaffScreen,
  StockScreen,
  SupportAccessScreen,
  SupportWorkScreen,
  TenantsScreen,
  TillHealthScreen,
  ZReportScreen,
} from "./lazy-screens";
import { SETTINGS_TABS, WORKSPACE_ROUTES, WORKSPACE_ROUTES_BY_PATH, Z_REPORT_ROUTE, type WorkspaceRoute } from "./route-manifest";

/** Routes with a real screen; the rest say what they are waiting for. */
const SCREENS: Partial<Record<string, React.ComponentType>> = {
  // The catalogue, read only (P5-013 W2), for Managers and Admins.
  "/catalogue": CatalogueScreen,
  // A Manager given Staff (P5-013 W6).
  "/cashiers": CashiersScreen,
  "/devices": DevicesScreen,
  // The overview (P5-004), for Managers and Admins since P5-013 W4.
  "/overview": OverviewScreen,
  "/platform/tenants": TenantsScreen,
  // Period reports (P5-013 W4), for Managers and Admins.
  "/reports": ReportsScreen,
  // Shifts and their Z reports (P5-004 slice D-4), for Managers and Admins.
  "/shifts": ShiftsScreen,
  // Receipt details (P5-013 W5a), for Managers and Admins.
  "/settings": ShopSettingsScreen,
  "/staff": StaffScreen,
  // Stock on hand and movements (P5-013 W3), for Managers and Admins.
  "/stock": StockScreen,
  "/support": SupportWorkScreen,
  "/support-access": SupportAccessScreen,
  // Till health (P5-013 W1), for Managers and Admins.
  "/tills": TillHealthScreen,
};

function screenFor(route: WorkspaceRoute) {
  const Screen = SCREENS[route.path];
  return Screen ? <Screen /> : <PlannedWorkspaceScreen route={route} />;
}

/**
 * The client route tree.
 *
 * Every workspace route is wrapped in `RouteAccessGate` from the same manifest
 * entry that produced its navigation link, so a route cannot be mounted without
 * declaring who it is drawn for. Typing a path directly into the address bar
 * goes through the identical check, which is why hiding a link is never the
 * mechanism that keeps somebody out.
 *
 * None of this is authorization. Ticket P1-014 owns that, on the server.
 */
export const appRoutes: RouteObject[] = [
  {
    children: [
      { element: <WorkspaceEntryScreen />, errorElement: <RouteErrorScreen />, index: true },
      { element: <SignInScreen />, errorElement: <RouteErrorScreen />, path: "sign-in" },
      // Reached from emailed links or the sign-in screen, before any session (P1-013).
      { element: <InvitationAcceptScreen />, errorElement: <RouteErrorScreen />, path: "invitations/accept" },
      { element: <PasswordResetScreen />, errorElement: <RouteErrorScreen />, path: "password-reset" },
      // The documents Admins and Managers accept (P1-023): public, readable
      // before signing in, and linked from the acceptance screen and the footer.
      { element: <LegalDocumentScreen document="terms" />, errorElement: <RouteErrorScreen />, path: "legal/terms" },
      { element: <LegalDocumentScreen document="privacy" />, errorElement: <RouteErrorScreen />, path: "legal/privacy" },
      { element: <AuthenticatorReplacementScreen />, errorElement: <RouteErrorScreen />, path: "replace-authenticator" },
      { element: <AssistedRecoveryScreen />, errorElement: <RouteErrorScreen />, path: "assisted-recovery" },
      {
        element: (
          <RouteAccessGate allowedRoles={WEB_WORKSPACE_ROLES}>
            <AccountScreen />
          </RouteAccessGate>
        ),
        errorElement: <RouteErrorScreen />,
        path: "account",
      },
      ...WORKSPACE_ROUTES.map(
        (route): RouteObject => ({
          element: (
            <RouteAccessGate allowedRoles={route.allowedRoles} permission={route.drawnWith}>
              {screenFor(route)}
            </RouteAccessGate>
          ),
          errorElement: <RouteErrorScreen />,
          path: route.path.replace(/^\//, ""),
        }),
      ),
      {
        // A closed shift's Z report, drawn for whoever may open /shifts.
        element: (
          <RouteAccessGate allowedRoles={WORKSPACE_ROUTES_BY_PATH.get(Z_REPORT_ROUTE.parent)!.allowedRoles}>
            <ZReportScreen />
          </RouteAccessGate>
        ),
        errorElement: <RouteErrorScreen />,
        path: Z_REPORT_ROUTE.path.replace(/^\//, ""),
      },
      {
        // Business details and the offline alert (P5-013 W5a): shop.settings,
        // an Admin's by role or a Manager's by grant (P5-013 W6).
        element: (
          <RouteAccessGate allowedRoles={SETTINGS_TABS.get("/settings/business")!.allowedRoles} permission="shop.settings">
            <BusinessSettingsScreen />
          </RouteAccessGate>
        ),
        errorElement: <RouteErrorScreen />,
        path: "settings/business",
      },
      {
        // The business's colours and logo (P5-014 BR1): shop.settings, as
        // business details are.
        element: (
          <RouteAccessGate allowedRoles={SETTINGS_TABS.get("/settings/branding")!.allowedRoles} permission="shop.settings">
            <BrandingSettingsScreen />
          </RouteAccessGate>
        ),
        errorElement: <RouteErrorScreen />,
        path: "settings/branding",
      },
      { element: <NotFoundScreen />, errorElement: <RouteErrorScreen />, path: "*" },
    ],
    element: <AppShell />,
    errorElement: <RouteErrorScreen />,
    path: "/",
  },
];
